import {
	Component,
	EventEmitter,
	Input,
	OnInit,
	Output,
	TemplateRef,
	ViewChild,
} from '@angular/core';
import { BaseClient, ClientType } from '@models/clients';
import { Select } from '@ngxs/store';
import {
	ClientSearchState,
	ClientSearchDispatch,
} from '@state/algolia-search/clientSearch';
import { AlgoliaHit } from '@state/utils/models';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import {
	DropdownResult,
	SelectionGroup,
} from '../../atomic/atoms/dropdown-options/dropdown-options.component';
import { AI_GENERATED_ICON, CloserTablerIcon, ICON } from '@core/util/icon';
import {
	PersonMatterClientWithAssociations,
	BusinessMatterClientWithAssociations,
} from '@models/matter';
import { map, startWith } from 'rxjs/operators';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { TestHooks } from './multiple-user-search.hooks';
import { SearchType } from 'src/app/public/leap/leap-api.service';
import { SelectedMatter } from '@state/matter/matter.selectors';

@Component({
	selector: 'app-multiple-user-search',
	templateUrl: './multiple-user-search.component.html',
	standalone: false,
})
export class MultipleUserSearchComponent implements OnInit {
	testHooks = TestHooks;
	//eslint-disable-next-line @typescript-eslint/no-explicit-any
	@ViewChild('createTemplate') createTemplate: TemplateRef<any>;
	//eslint-disable-next-line @typescript-eslint/no-explicit-any
	@ViewChild('itemTemplate') itemTemplate: TemplateRef<any>;
	@ViewChild('searchingLeapDbTemplate')
	//eslint-disable-next-line @typescript-eslint/no-explicit-any
	searchingLeapDbTemplate: TemplateRef<any>;

	public selectedIds: string[] = [];
	public showDropdown = false;
	public ICON = ICON;
	public searchValue$ = new BehaviorSubject<string>('');
	public selected: BaseClient;
	public clientTypes = ClientType;
	public itemGroups$: Observable<SelectionGroup[]>;
	public showLoading$: Observable<boolean>;
	public AI_ICON = AI_GENERATED_ICON.icon;
	public AI_TOOLTIP = AI_GENERATED_ICON.tooltip;

	@Input() label: string;
	@Input() selectedClients: (
		| PersonMatterClientWithAssociations
		| BusinessMatterClientWithAssociations
	)[] = [];
	@Input() disabled: boolean = false;
	@Input() skeletonLoading: boolean = false;
	@Input() labelIcon: ICON | CloserTablerIcon;
	@Input() labelToolTip: string;
	@Input() labelIconClasses: string;

	@Output() add: EventEmitter<BaseClient> = new EventEmitter<BaseClient>();
	@Output() remove: EventEmitter<string> = new EventEmitter<string>();
	@Output() create: EventEmitter<string> = new EventEmitter<string>();
	@Output() onSelect: EventEmitter<string> = new EventEmitter<string>();
	@Output() onInputFocus: EventEmitter<string> = new EventEmitter<string>();

	@Select(ClientSearchState.searching) searching$: Observable<boolean>;

	@Select(ClientSearchState.list) searchClients$: Observable<
		AlgoliaHit<BaseClient>[]
	>;

	@SelectSnapshot(SelectedMatter.isLeapMatter) isLeapMatter: boolean;

	constructor(private clientSearchDispatch: ClientSearchDispatch) {}

	ngOnInit(): void {
		this.clientSearchDispatch.clearResults();

		this.showLoading$ = this.searching$.pipe(
			map((searching) => searching && !this.isLeapMatter),
		);

		this.itemGroups$ = combineLatest([
			this.searchValue$,
			this.searching$,
			this.searchClients$,
		]).pipe(
			startWith<[string, boolean, AlgoliaHit<BaseClient>[]]>(['', false, []]),
			map(([searchValue, searching, searchClients]) => {
				if (searching && this.isLeapMatter) {
					return [
						{
							items: [
								{ text: 'Searching LEAP', spin: true, icon: ICON.LOADING },
							],
							template: this.searchingLeapDbTemplate,
							hover: false,
						},
					];
				}

				return [
					{
						items: [searchValue],
						template: this.createTemplate,
					},
					{
						items: searchClients,
						template: this.itemTemplate,
						title: 'Search results',
						noResultsMessage: 'No clients found',
					},
				];
			}),
		);
	}

	_onSelect($event: DropdownResult<BaseClient>): void {
		if ($event.group === 1) {
			this.add.emit($event.result);
		}

		this.showDropdown = false;
		this.searchValue$.next('');
	}

	_create() {
		const searchValue = this.searchValue$.getValue();
		this.create.emit(searchValue);
	}

	_remove(id: string) {
		this.remove.emit(id);
	}

	debounce(searchValue: string) {
		if (!this.showDropdown) {
			this.showDropdown = true;
		}
		if (searchValue.length > 0) {
			this.clientSearchDispatch.search(searchValue, SearchType.CardsAndPeople);
		}
	}

	selectPill(client: BaseClient) {
		this.onSelect.emit(client.id);
	}

	handleOnInputFocus() {
		this.onInputFocus.emit(this.label);
	}

	trackByClientId(_: number, client: BaseClient): string {
		return client.id;
	}

	updateSearchValue(newSearchValue: string) {
		this.searchValue$.next(newSearchValue);
	}
}
